import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getMissedVisitsReasonsSchema } from '@/types/schemas/routes/getMissedVisitsReasons';
import type {
  MissedVisitsReasons,
  Route,
  RouteDefinitions,
  RouteTraderSite,
  VisitPlan,
} from '@/types/Routes';
import { getRoutesSchema, routeSchema } from '@/types/schemas/routes/getRoutesSchema';
import { getRouteDefinitionsSchema } from '@/types/schemas/routes/getRouteDefinitions';
import { getVisitPlansSchema } from '@/types/schemas/routes/getVisitPlansSchema';
import { getRouteTraderSitesSchema } from '@/types/schemas/routes/getRouteTraderSites';

export async function getMissedVisitsReasons() {
  const response = await axios.get('/v2/visits/missed-reasons');
  return validate(getMissedVisitsReasonsSchema, response.data.data);
}

export async function editMissedVisitReason(missedVisitReason: MissedVisitsReasons) {
  const response = await axios.post('/v2/visits/missed-reasons', missedVisitReason);
  if (response.status === 201) return true;
  return false;
}

export async function removeMissedVisitsReasonsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/missed-reasons/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getVisitPlans() {
  const response = await axios.get('/v2/visits/visit-plans');
  return validate(getVisitPlansSchema, response.data.data);
}

export async function removeVisitPlansByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/visit-plans/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function editVisitPlan(visitPlan: VisitPlan) {
  const response = await axios.post('/v2/visits/visit-plans', visitPlan);
  if (response.status === 201) return true;
  return false;
}

export async function getRoutes(): Promise<Partial<Route>[]> {
  const response = await axios.get('/v2/visits/routes');
  return validate(getRoutesSchema, response.data.data);
}

export async function getRouteById(routeId: string): Promise<Partial<Route> | null> {
  const response = await axios.get(`/v2/visits/routes/${routeId}`);
  return validate(routeSchema, response.data);
}

export async function removeRoutesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/routes/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function editRoute(route: Route) {
  const response = await axios.post('/v2/visits/routes', route);
  if (response.status === 201) return true;
  return false;
}

export async function getRouteDefinitions(): Promise<RouteDefinitions[]> {
  const response = await axios.get('/v2/visits/routeDefinitions');
  return validate(getRouteDefinitionsSchema, response.data.data);
}

export async function addOrEditRouteDefinition(item: RouteDefinitions): Promise<boolean> {
  const response = await axios.post('/v2/visits/routeDefinitions', item);
  if (response.status === 201) return true;
  return false;
}

export async function removeRouteDefinitionsByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/routeDefinitions/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getRouteTraderSites(routeId: string): Promise<RouteTraderSite[]> {
  const response = await axios.get(`/v2/visits/routeTraderSites/${routeId}`);
  return validate(getRouteTraderSitesSchema, response.data);
}

export async function removeRouteTraderSitesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/routeTraderSites/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function restoreRouteTraderSiteById(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/visits/routeTraderSites/restore-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });

  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addEditRouteTraderSites(items: RouteTraderSite[]): Promise<boolean> {
  const response = await axios.post('/v2/visits/routeTraderSites', items);
  if (response.status === 201) return true;
  return false;
}
